import cloneDeep from 'lodash/cloneDeep';
import sortBy from 'lodash/sortBy';

import { mapCategory, mapCol, mapCondition, mapRow } from '@webapp/common/lib/map';
import { undefDestroy } from '@webapp/common/lib/utils';
import type { ForwardLogic, Question } from '@webapp/common/resources/entities/question';
import { initialQuestion } from '@webapp/common/resources/entities/question';
import { initialQuestionParams } from '@webapp/common/resources/question';
import type { QuestionType } from '@webapp/common/resources/survey';

export const mapForwardLogic = (data: {
    id: number;
    questionId: number;
    childQuestionId: number;
    checked: number;
    details: Array<any>;
    type: string;
}): ForwardLogic => ({
    ...data,
    checked: Boolean(data.checked),
});

export const mapQuestion = (data: any, skipAdditional?: boolean): Question => {
    const {
        answers,
        groups,
        categories,
        ID,
        logics,
        forwardLogic,
        PAGE,
        parameters: { params },
        QUESTION,
        QUESTION_TYPE_ID,
        MAXDIFF_BLOCK_COUNT,
        TARGET_REPETITIONS_OPTIONS,
        SERIAL,
        FILE_ID,
        ORDER,
        FILE_PATH,
        PINNED,
        conjointConfig,
    } = data;
    const question: Question = {
        ...(!skipAdditional
            ? { ...cloneDeep(initialQuestion), typeMode: false }
            : { params: cloneDeep(initialQuestionParams) }),
        id: ID,
        name: QUESTION === null ? '' : QUESTION,
        pageId: PAGE,
        fileId: FILE_ID,
        filePath: FILE_PATH,
        type: QUESTION_TYPE_ID as QuestionType,
        rows: answers.map(mapRow),
        cols: groups.map(mapCol),
        conditions: logics.map((logic: any) => mapCondition(logic, data)),
        serial: SERIAL,
        forwardLogic: (forwardLogic || []).map(mapForwardLogic),
        order: ORDER,
        pinned: PINNED === 1,
        categories: sortBy(categories?.map(mapCategory), 'order'),
        maxDiffBlockCount: MAXDIFF_BLOCK_COUNT,
        targetRepetitionsOptions: TARGET_REPETITIONS_OPTIONS,
        conjointConfig,
    };

    if (params) {
        try {
            question.params = Object.assign(cloneDeep(initialQuestion.params), {
                scaleType: 'custom',
                ...JSON.parse(params),
            });
        } catch (e) {
            console.error(e);
        }
    }

    return question;
};

export const reverseMapRow = ({
    exception,
    fileId,
    order,
    type,
    videoLink,
    textBlock,
    pinned,
    answerArea,
    excluded,
}: any): any => {
    return undefDestroy({
        file_id: fileId,
        video_link: videoLink,
        textBlock,
        order,
        type,
        pinned: pinned !== undefined ? Number(pinned) : undefined,
        excluded: excluded !== undefined ? Number(excluded) : undefined,
        exception: exception !== undefined ? Number(exception) : undefined,
        answerArea,
    });
};
export const reverseMapQuestion = ({ name, type, fileId, pinned }: any): any =>
    undefDestroy({
        question: name || name === '' ? name : undefined,
        type,
        file_id: fileId,
        pinned: pinned !== undefined ? Number(pinned) : undefined,
    });

export const reverseMapCol = (col: any): any => {
    if (col) {
        const { answers, exception, name, type, value, order, numericValue, pinned } = col;
        return undefDestroy({
            value,
            name,
            answers: answers ? JSON.stringify(answers) : undefined,
            type,
            order,
            numeric_value: numericValue,
            exception: exception !== undefined ? Number(exception) : undefined,
            pinned: pinned !== undefined ? Number(pinned) : undefined,
        });
    }

    return {
        value: '',
        answers: [],
    };
};
